/**
 * IT Service Request Dashboard Page
 * @description IT 서비스 요청 대시보드 - 통계 및 빠른 액세스
 */

import { useEffect, useState } from 'react';
import { useNavigate } from '@shared/router';
import { Badge, Button, Card } from '@shared/ui/primitives';
import { useAuthStore } from '@/stores/authStore';
import {
    ClipboardList,
    AlertCircle,
    GitBranch,
    Clock,
    CheckCircle,
    XCircle,
    Plus,
    TrendingUp,
    Users,
    Activity,
    Calendar,
    ArrowRight,
    Zap,
    Shield,
    Settings,
} from 'lucide-react';
import { getTickets } from '@/services/ticketApi';
import type { Ticket } from '@/types/ticket.types';

export const ITServiceDashboardPage: React.FC = () => {
    const navigate = useNavigate();
    const user = useAuthStore((state) => state.user);

    const [stats, setStats] = useState({
        myOpenTickets: 0,
        myAssignedTickets: 0,
        approvalPending: 0,
        totalOpen: 0,
        recentTickets: [] as Ticket[],
    });
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        loadStats();
    }, [user]);

    const loadStats = async () => {
        if (!user || user.userType !== 'tenant-user') {
            setIsLoading(false);
            return;
        }

        try {
            setIsLoading(true);

            // 내 진행중 티켓
            const myTickets = await getTickets(user.tenantId, {
                reporter: user.userId,
                status: 'in_progress',
            });

            // 내 담당 티켓
            const assignedTickets = await getTickets(user.tenantId, {
                assignee: user.userId,
            });

            // 승인 대기 (간단히 pending 상태로 조회)
            const pendingTickets = await getTickets(user.tenantId, {
                status: 'pending',
            });

            // 전체 진행중 티켓
            const allOpenTickets = await getTickets(user.tenantId, {
                status: 'in_progress',
            });

            // 최근 티켓 (최대 5개)
            const recentTickets = await getTickets(user.tenantId, {
                size: 5,
                sortBy: 'createdAt',
                sortOrder: 'desc',
            });

            setStats({
                myOpenTickets: myTickets.total,
                myAssignedTickets: assignedTickets.total,
                approvalPending: pendingTickets.total,
                totalOpen: allOpenTickets.total,
                recentTickets: recentTickets.tickets,
            });
        } catch (error) {
            console.error('Failed to load stats:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleNavigate = (path: string) => {
        navigate({ to: path as any });
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'in_progress':
                return 'bg-blue-100 text-blue-800';
            case 'pending':
                return 'bg-yellow-100 text-yellow-800';
            case 'resolved':
                return 'bg-green-100 text-green-800';
            case 'closed':
                return 'bg-gray-100 text-gray-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const getPriorityColor = (priority: string) => {
        switch (priority) {
            case 'critical':
                return 'bg-red-100 text-red-800';
            case 'high':
                return 'bg-orange-100 text-orange-800';
            case 'medium':
                return 'bg-blue-100 text-blue-800';
            case 'low':
                return 'bg-gray-100 text-gray-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    if (user?.userType !== 'tenant-user') {
        return (
            <div className="flex items-center justify-center h-64">
                <p className="text-muted-foreground">Tenant 사용자만 접근 가능합니다.</p>
            </div>
        );
    }

    return (
        <div className="space-y-8">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-foreground">IT 서비스 요청</h1>
                    <p className="text-muted-foreground mt-2">
                        티켓 관리 및 승인 워크플로우를 통한 효율적인 IT 서비스 운영
                    </p>
                </div>
                <div className="flex gap-3">
                    <Button
                        onClick={() => handleNavigate('/it-service/servicerequests/new')}
                        className="gap-2 bg-blue-600 hover:bg-blue-700"
                        size="lg"
                    >
                        <Plus className="w-5 h-5" />
                        새 서비스 요청
                    </Button>
                    <Button
                        onClick={() => handleNavigate('/it-service/incidents/new')}
                        variant="outline"
                        className="gap-2 border-red-200 text-red-600 hover:bg-red-50"
                        size="lg"
                    >
                        <AlertCircle className="w-5 h-5" />
                        장애 보고
                    </Button>
                    <Button
                        onClick={() => handleNavigate('/it-service/changerequests/new')}
                        variant="outline"
                        className="gap-2 border-green-200 text-green-600 hover:bg-green-50"
                        size="lg"
                    >
                        <GitBranch className="w-5 h-5" />
                        변경 요청
                    </Button>
                </div>
            </div>

            {/* Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <Card
                    className="p-6 cursor-pointer hover:shadow-lg transition-all duration-200 border-l-4 border-l-blue-500 bg-gradient-to-r from-blue-50 to-white"
                    onClick={() => handleNavigate('/it-service/my-tickets')}
                >
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-blue-600 mb-1">내 진행중 티켓</p>
                            <p className="text-3xl font-bold text-blue-700">
                                {isLoading ? '-' : stats.myOpenTickets}
                            </p>
                            <p className="text-xs text-blue-500 mt-1">내가 생성한 티켓</p>
                        </div>
                        <div className="p-3 bg-blue-100 rounded-full">
                            <Clock className="w-8 h-8 text-blue-600" />
                        </div>
                    </div>
                </Card>

                <Card
                    className="p-6 cursor-pointer hover:shadow-lg transition-all duration-200 border-l-4 border-l-green-500 bg-gradient-to-r from-green-50 to-white"
                    onClick={() => handleNavigate('/it-service/my-assigned-tickets')}
                >
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-green-600 mb-1">내 담당 티켓</p>
                            <p className="text-3xl font-bold text-green-700">
                                {isLoading ? '-' : stats.myAssignedTickets}
                            </p>
                            <p className="text-xs text-green-500 mt-1">처리해야 할 티켓</p>
                        </div>
                        <div className="p-3 bg-green-100 rounded-full">
                            <Users className="w-8 h-8 text-green-600" />
                        </div>
                    </div>
                </Card>

                <Card
                    className="p-6 cursor-pointer hover:shadow-lg transition-all duration-200 border-l-4 border-l-orange-500 bg-gradient-to-r from-orange-50 to-white"
                    onClick={() => handleNavigate('/it-service/approval-pending')}
                >
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-orange-600 mb-1">승인 대기</p>
                            <p className="text-3xl font-bold text-orange-700">
                                {isLoading ? '-' : stats.approvalPending}
                            </p>
                            <p className="text-xs text-orange-500 mt-1">승인이 필요한 티켓</p>
                        </div>
                        <div className="p-3 bg-orange-100 rounded-full">
                            <Shield className="w-8 h-8 text-orange-600" />
                        </div>
                    </div>
                </Card>

                <Card className="p-6 border-l-4 border-l-purple-500 bg-gradient-to-r from-purple-50 to-white">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-purple-600 mb-1">전체 진행중</p>
                            <p className="text-3xl font-bold text-purple-700">
                                {isLoading ? '-' : stats.totalOpen}
                            </p>
                            <p className="text-xs text-purple-500 mt-1">시스템 전체 현황</p>
                        </div>
                        <div className="p-3 bg-purple-100 rounded-full">
                            <Activity className="w-8 h-8 text-purple-600" />
                        </div>
                    </div>
                </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Quick Access */}
                <div className="lg:col-span-2">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-xl font-semibold text-foreground">빠른 액세스</h2>
                        <Button variant="ghost" size="sm" className="text-muted-foreground">
                            전체 보기 <ArrowRight className="w-4 h-4 ml-1" />
                        </Button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Card
                            className="p-6 cursor-pointer hover:shadow-lg transition-all duration-200 group border-blue-100 hover:border-blue-200"
                            onClick={() => handleNavigate('/it-service/servicerequests')}
                        >
                            <div className="flex items-center gap-4">
                                <div className="p-3 bg-blue-100 rounded-lg group-hover:bg-blue-200 transition-colors">
                                    <ClipboardList className="w-8 h-8 text-blue-600" />
                                </div>
                                <div className="flex-1">
                                    <p className="font-semibold text-foreground text-lg">서비스 요청</p>
                                    <p className="text-sm text-muted-foreground">Service Requests</p>
                                    <p className="text-xs text-blue-600 mt-1">새로운 서비스나 리소스 요청</p>
                                </div>
                                <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-blue-600 transition-colors" />
                            </div>
                        </Card>

                        <Card
                            className="p-6 cursor-pointer hover:shadow-lg transition-all duration-200 group border-red-100 hover:border-red-200"
                            onClick={() => handleNavigate('/it-service/incidents')}
                        >
                            <div className="flex items-center gap-4">
                                <div className="p-3 bg-red-100 rounded-lg group-hover:bg-red-200 transition-colors">
                                    <AlertCircle className="w-8 h-8 text-red-600" />
                                </div>
                                <div className="flex-1">
                                    <p className="font-semibold text-foreground text-lg">장애 관리</p>
                                    <p className="text-sm text-muted-foreground">Incidents</p>
                                    <p className="text-xs text-red-600 mt-1">시스템 장애 및 문제 해결</p>
                                </div>
                                <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-red-600 transition-colors" />
                            </div>
                        </Card>

                        <Card
                            className="p-6 cursor-pointer hover:shadow-lg transition-all duration-200 group border-green-100 hover:border-green-200"
                            onClick={() => handleNavigate('/it-service/changerequests')}
                        >
                            <div className="flex items-center gap-4">
                                <div className="p-3 bg-green-100 rounded-lg group-hover:bg-green-200 transition-colors">
                                    <GitBranch className="w-8 h-8 text-green-600" />
                                </div>
                                <div className="flex-1">
                                    <p className="font-semibold text-foreground text-lg">변경 관리</p>
                                    <p className="text-sm text-muted-foreground">Change Requests</p>
                                    <p className="text-xs text-green-600 mt-1">시스템 변경 및 업데이트</p>
                                </div>
                                <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-green-600 transition-colors" />
                            </div>
                        </Card>

                        <Card
                            className="p-6 cursor-pointer hover:shadow-lg transition-all duration-200 group border-purple-100 hover:border-purple-200"
                            onClick={() => handleNavigate('/it-service/reports')}
                        >
                            <div className="flex items-center gap-4">
                                <div className="p-3 bg-purple-100 rounded-lg group-hover:bg-purple-200 transition-colors">
                                    <TrendingUp className="w-8 h-8 text-purple-600" />
                                </div>
                                <div className="flex-1">
                                    <p className="font-semibold text-foreground text-lg">리포트</p>
                                    <p className="text-sm text-muted-foreground">Reports & Analytics</p>
                                    <p className="text-xs text-purple-600 mt-1">성과 분석 및 통계</p>
                                </div>
                                <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-purple-600 transition-colors" />
                            </div>
                        </Card>
                    </div>
                </div>

                {/* Recent Activity */}
                <div>
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-xl font-semibold text-foreground">최근 활동</h2>
                        <Button variant="ghost" size="sm" className="text-muted-foreground">
                            전체 보기 <ArrowRight className="w-4 h-4 ml-1" />
                        </Button>
                    </div>
                    <Card className="p-6">
                        {isLoading ? (
                            <div className="space-y-4">
                                {[...Array(3)].map((_, i) => (
                                    <div key={i} className="animate-pulse">
                                        <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                                        <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                                    </div>
                                ))}
                            </div>
                        ) : stats.recentTickets.length === 0 ? (
                            <div className="text-center py-8">
                                <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                                <p className="text-muted-foreground">최근 활동이 없습니다</p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {stats.recentTickets.map((ticket) => (
                                    <div
                                        key={ticket.ticketId}
                                        className="flex items-start gap-3 p-3 rounded-lg hover:bg-accent cursor-pointer transition-colors"
                                        onClick={() => handleNavigate(`/it-service/tickets/${ticket.ticketId}`)}
                                    >
                                        <div className="p-2 bg-blue-100 rounded-full">
                                            <ClipboardList className="w-4 h-4 text-blue-600" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium text-sm text-foreground truncate">
                                                {ticket.title}
                                            </p>
                                            <div className="flex items-center gap-2 mt-1">
                                                <Badge variant="outline" className={getStatusColor(ticket.status)}>
                                                    {ticket.status}
                                                </Badge>
                                                <Badge variant="outline" className={getPriorityColor(ticket.priority)}>
                                                    {ticket.priority}
                                                </Badge>
                                            </div>
                                            <p className="text-xs text-muted-foreground mt-1">
                                                {new Date(ticket.createdAt).toLocaleDateString()}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </Card>
                </div>
            </div>
        </div>
    );
};

ITServiceDashboardPage.displayName = 'ITServiceDashboardPage';
